import type { ViewKpiAppearance } from '@salescore/core'
import { Empty } from 'antd'
import { Array, Order, pipe, Record } from 'effect'

import {
  calculateUnit,
  numberWithDelimiterFilter,
  numberWithFixedDecimal,
  unitLabel as getUnitLabel,
} from '../../misc/filters'
import { assignColors } from './assignColors'
import {
  type ChartLabelDisplayModeValue,
  type ChartLegendsDisplayModeValue,
  EChart,
  type EChartProperties,
} from './EChart'

export const EChartPie = (
  properties: Omit<EChartProperties, 'option'> & {
    records: Array<Record<string, unknown>>
    ratioKey: string
    valueKey: string
    groupKey: string
    ratioLabelKey?: string
    groupLabelKey?: string
    groupFilters?: string[]
    valueLabelDisplayMode?: ChartLabelDisplayModeValue
    valueLabelUnitType?: ViewKpiAppearance['unitType']
    valueLabelDecimalPlaceType?: ViewKpiAppearance['decimalPlaceType']
    legendDisplayMode?: ChartLegendsDisplayModeValue
  },
) => {
  const {
    records,
    ratioKey,
    valueKey,
    groupKey,
    ratioLabelKey,
    groupLabelKey,
    groupFilters,
    valueLabelDisplayMode,
    valueLabelUnitType,
    valueLabelDecimalPlaceType,
    legendDisplayMode,
  } = properties
  const keys = pipe(
    records,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Array.groupBy((x) => x[ratioKey] as string),
    Record.keys,
  )
  const colorDict = assignColors(keys)
  const grouped = pipe(
    records,
    Array.filter((x) => {
      if (groupFilters === undefined || groupFilters.length === 0) {
        return true
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return groupFilters.includes(x[groupKey] as string)
    }),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
    Array.groupBy((x) => (x[groupKey] as string) ?? ''),
  )
  const keyToValue =
    groupLabelKey === undefined
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ({} as Record<string, string>)
      : Record.map(grouped, (vs) => vs[0][groupLabelKey])

  if (records.length === 0) {
    return <Empty />
  }

  return (
    <div>
      {pipe(
        grouped,
        Record.toEntries,
        Array.map(([groupKeyValue, records]) => {
          const data = pipe(
            records,
            Array.map((record) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              const key = record[ratioKey] as string
              return {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                value: record[valueKey] as number,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,@typescript-eslint/no-unsafe-type-assertion
                name: (record[ratioLabelKey ?? ratioKey] as string) ?? (record[ratioKey] as string),
                key, // keyという項目は本来不要だが、ドリルダウンのためにデータ型を拡張している
                groupKey: groupKeyValue,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,@typescript-eslint/no-unsafe-type-assertion
                groupLabel: (keyToValue[groupKeyValue] as string) ?? groupKeyValue,
                itemStyle: {
                  color: colorDict[key],
                },
              }
            }),
            Array.filter((x) => x.value > 0),
            Array.sortBy(
              Order.combine(
                Order.mapInput(Order.String, (x) => x.groupLabel),
                Order.mapInput(Order.Number, (x) => -x.value),
              ),
            ),
          )
          const series = [
            {
              name: '', // このnameも表示されるが、何を書くべきか？
              type: 'pie' as const,
              radius: '50%',
              data,
              label: {
                show: valueLabelDisplayMode !== 'none',
                formatter: (p: unknown) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  const parameter = p as { name: string; value: number | string; color: string; percent: number }
                  const { name, value, percent } = parameter

                  const displayValue = generateDisplayValue({
                    value,
                    percent,
                    shouldIgnoreSmallValue: true,
                    valueLabelDisplayMode,
                    valueLabelUnitType,
                    valueLabelDecimalPlaceType,
                  })

                  if (displayValue === '') {
                    return ''
                  }

                  return `${name}: ${displayValue}`
                },
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
            },
          ]
          return {
            data,
            series,
            groupKeyValue,
            records,
          }
        }),
        Array.sortBy(Order.mapInput(Order.String, ({ data, groupKeyValue }) => data[0]?.groupLabel ?? groupKeyValue)),
        Array.map(({ series, groupKeyValue }) => (
          <div key={groupKeyValue}>
            <EChart
              {...properties}
              height={600}
              option={{
                title: {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
                  text: (keyToValue[groupKeyValue] as string) ?? groupKeyValue,
                  // subtext: 'Fake Data',
                  left: 'center',
                },
                tooltip: {
                  trigger: 'item',
                  // https://echarts.apache.org/en/option.html#tooltip.formatter
                  formatter: (p: unknown) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    const parameters = p as { percent: number; name: string; value: number | string; color: string }
                    const displayValue = generateDisplayValue({
                      value: parameters.value,
                      percent: parameters.percent,
                      valueLabelDisplayMode,
                      valueLabelUnitType,
                      valueLabelDecimalPlaceType,
                    })

                    if (displayValue === '') {
                      return ''
                    }

                    return `${parameters.name}  ${displayValue}`
                  },
                },
                legend:
                  legendDisplayMode === 'none'
                    ? undefined
                    : {
                        type: 'scroll',
                        orient: 'vertical',
                        left: 'left',
                        // 凡例が多い時に次のグラフのタイトルに被らないように下方向にpaddingを追加
                        padding: [0, 0, 80, 0],
                        textStyle: {
                          width: 200,
                          overflow: 'breakAll',
                        },
                        z: 1,
                      },
                series,
              }}
            />
          </div>
        )),
      )}
    </div>
  )
}

function generateDisplayValue({
  value,
  percent,
  shouldIgnoreSmallValue,
  valueLabelDisplayMode,
  valueLabelUnitType,
  valueLabelDecimalPlaceType,
}: {
  value: number | string
  percent: number
  shouldIgnoreSmallValue?: boolean
  valueLabelDisplayMode?: ChartLabelDisplayModeValue
  valueLabelUnitType?: ViewKpiAppearance['unitType']
  valueLabelDecimalPlaceType?: ViewKpiAppearance['decimalPlaceType']
}) {
  const numberValue = Number(value)

  if (Number.isNaN(numberValue)) {
    return `${value}`
  }

  if (numberValue === 0) {
    return ``
  }

  if (valueLabelDisplayMode !== 'showAll' && shouldIgnoreSmallValue === true && percent < 3) {
    return ``
  }

  const valueConvertedInUnit = calculateUnit(numberValue, valueLabelUnitType, undefined)
  const valueRoundedToDecimal = numberWithFixedDecimal(valueConvertedInUnit, valueLabelDecimalPlaceType, undefined)
  const percentRoundedToDecimal = numberWithFixedDecimal(percent, valueLabelDecimalPlaceType, undefined)
  const unitLabel = getUnitLabel(numberValue, valueLabelUnitType, undefined)
  const displayUnitLabel = unitLabel === '' ? '' : ` ${unitLabel}`

  return `${numberWithDelimiterFilter(valueRoundedToDecimal)}${displayUnitLabel} (${percentRoundedToDecimal}%)`
}
